import React, { useMemo, useState, useEffect } from 'react';
import { 
  useStates, 
  useTargetScopes, 
  useRegionalPriorities, 
  useStateLanguages,
  UseRegionalSelectionReturn 
} from '../../hooks/useRegional';
import { State, StateLanguage } from '../../types/regionalTypes';
import Button from '../bootstrap/Button';
import Icon from '../icon/Icon';
import Select from '../bootstrap/forms/Select';
import Option from '../bootstrap/Option';
import '../../styles/components/RegionalSelector.scss';

interface RegionalSelectorProps {
  selection: UseRegionalSelectionReturn;
  className?: string;
  showQuickSelect?: boolean;
  maxHeight?: string;
}

const RegionalSelector: React.FC<RegionalSelectorProps> = ({
  selection,
  className = '',
  showQuickSelect = true,
  maxHeight = '400px'
}) => {
  const { states, isLoading: statesLoading, error: statesError } = useStates();
  const { targetScopes, isLoading: scopesLoading, error: scopesError } = useTargetScopes();
  const { regionalPriorities, isLoading: prioritiesLoading, error: prioritiesError } = useRegionalPriorities();
  const { languages, isLoading: languagesLoading, refetch: fetchLanguages } = useStateLanguages();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Ensure states is always an array to prevent filter errors
  const safeStates = Array.isArray(states) ? states : [];

  // Get commonly used states for quick select
  const majorStates = useMemo(() => {
    return safeStates.filter(state => 
      ['Maharashtra', 'Karnataka', 'Tamil Nadu', 'Delhi', 'West Bengal', 'Gujarat', 'Rajasthan', 'Uttar Pradesh']
      .includes(state.stateName)
    );
  }, [safeStates]);

  const selectedStatesData = useMemo(() => {
    return selection.getSelectedStates(safeStates);
  }, [safeStates, selection]);

  const primaryStateData = useMemo(() => {
    return selection.getPrimaryState(safeStates);
  }, [safeStates, selection]);

  // Fetch languages when primary state changes
  useEffect(() => {
    if (selection.selection.primaryStateId) {
      fetchLanguages(selection.selection.primaryStateId);
    }
  }, [selection.selection.primaryStateId, fetchLanguages]);

  // Filter states based on search term
  const filteredStates = useMemo(() => {
    if (!searchTerm) return safeStates;
    return safeStates.filter(state => 
      state.stateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      state.stateCode.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [safeStates, searchTerm]);

  const renderQuickSelectStates = () => {
    if (!showQuickSelect || majorStates.length === 0) return null;

    return (
      <div className="quick-select-states">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="regional-selector__title mb-0">Quick Select States</h6>
          <Button
            color="light"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            icon={isExpanded ? 'ExpandLess' : 'ExpandMore'}
          >
            {isExpanded ? 'Collapse' : 'Show All States'}
          </Button>
        </div>
        <div className="quick-states-grid">
          {majorStates.map(state => (
            <button
              key={state.stateId}
              className={`quick-state-btn ${
                selection.isStateSelected(state.stateId) ? 'quick-state-btn--selected' : ''
              }`}
              onClick={() => {
                selection.toggleState(state.stateId);
                // Auto-set as primary if it's the first selection
                if (!selection.selection.primaryStateId && !selection.isStateSelected(state.stateId)) {
                  selection.setPrimaryState(state.stateId);
                }
              }}
              type="button"
            >
              <div className="quick-state-btn__content">
                <span className="quick-state-btn__name">{state.stateName}</span>
                <small className="quick-state-btn__code">{state.stateCode}</small>
                {selection.isPrimaryState(state.stateId) && (
                  <div className="quick-state-btn__primary">
                    <Icon icon="Star" size="sm" />
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderAllStates = () => {
    if (!isExpanded) return null;
    
    return (
      <div className="all-states mt-3">
        <h6 className="regional-selector__title">All States</h6>
        
        {/* Search input */}
        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search states..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="states-grid" style={{ maxHeight, overflowY: 'auto' }}>
          <div className="row g-2">
            {filteredStates.map(state => (
              <div key={state.stateId} className="col-md-6 col-lg-4">
                <button
                  className={`state-btn w-100 ${
                    selection.isStateSelected(state.stateId) ? 'state-btn--selected' : ''
                  }`}
                  onClick={() => {
                    selection.toggleState(state.stateId);
                    // Auto-set as primary if it's the first selection
                    if (!selection.selection.primaryStateId && !selection.isStateSelected(state.stateId)) {
                      selection.setPrimaryState(state.stateId);
                    }
                  }}
                  type="button"
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="text-start">
                      <div className="state-btn__name">{state.stateName}</div>
                      <small className="text-muted">{state.stateCode}</small>
                    </div>
                    {selection.isPrimaryState(state.stateId) && (
                      <Icon icon="Star" color="warning" size="sm" />
                    )}
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderConfigurationOptions = () => {
    return (
      <div className="regional-config-options mt-3">
        <div className="row">
          {/* Target Scope */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Target Scope *</label>
            {scopesError && (
              <div className="alert alert-danger mb-2" role="alert">
                <small>{scopesError}</small>
              </div>
            )}
            <Select
              value={selection.selection.targetScopeId?.toString() || ''}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => selection.setTargetScope(Number(e.target.value))}
              ariaLabel="Select Target Scope"
              placeholder="Select Target Scope"
              disabled={scopesLoading}
            >
              {scopesLoading ? (
                <Option value="" disabled>Loading scopes...</Option>
              ) : (
                <>
                  <Option value="">Select Target Scope</Option>
                  {targetScopes
                    .filter(scope => scope.isActive)
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map(scope => (
                      <Option key={scope.scopeId} value={scope.scopeId.toString()}>
                        {`${scope.scopeName} - ${scope.scopeDescription}`}
                      </Option>
                    ))}
                </>
              )}
            </Select>
          </div>

          {/* Regional Priority */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Content Priority *</label>
            {prioritiesError && (
              <div className="alert alert-danger mb-2" role="alert">
                <small>{prioritiesError}</small>
              </div>
            )}
            <Select
              value={selection.selection.regionalPriorityId?.toString() || ''}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => selection.setRegionalPriority(Number(e.target.value))}
              ariaLabel="Select Regional Priority"
              placeholder="Select Priority"
              disabled={prioritiesLoading}
            >
              {prioritiesLoading ? (
                <Option value="" disabled>Loading priorities...</Option>
              ) : (
                <>
                  <Option value="">Select Priority</Option>
                  {regionalPriorities
                    .filter(priority => priority.isActive)
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map(priority => (
                      <Option key={priority.priorityId} value={priority.priorityId.toString()}>
                        {`${priority.priorityName} - ${priority.priorityDescription}`}
                      </Option>
                    ))}
                </>
              )}
            </Select>
          </div>
        </div>

        {/* Primary State Selection (if multiple states selected) */}
        {selectedStatesData.length > 1 && (
          <div className="mb-3">
            <label className="form-label">Primary State</label>
            <Select
              value={selection.selection.primaryStateId?.toString() || ''}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => selection.setPrimaryState(Number(e.target.value))}
              ariaLabel="Select Primary State"
              placeholder="Select Primary State"
            >
              <Option value="">Select Primary State</Option>
              {selectedStatesData.map((state: State) => (
                <Option key={state.stateId} value={state.stateId.toString()}>
                  {state.stateName}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Language Selection (if primary state is selected) */}
        {selection.selection.primaryStateId && languages.length > 0 && (
          <div className="mb-3">
            <label className="form-label">Content Language</label>
            <Select
              value={selection.selection.languageId?.toString() || ''}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => selection.setLanguage(Number(e.target.value))}
              ariaLabel="Select Language"
              placeholder="Select Language"
              disabled={languagesLoading}
            >
              {languagesLoading ? (
                <Option value="" disabled>Loading languages...</Option>
              ) : (
                <>
                  <Option value="">Select Language</Option>
                  {languages
                    .sort((a, b) => {
                      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
                      return a.displayOrder - b.displayOrder;
                    })
                    .map(language => (
                      <Option key={language.languageId} value={language.languageId.toString()}>
                        {`${language.languageName} (${language.languageNativeName})${language.isPrimary ? ' - Primary' : ''}`}
                      </Option>
                    ))}
                </>
              )}
            </Select>
          </div>
        )}
      </div>
    );
  };

  const renderSelectionSummary = () => {
    if (selectedStatesData.length === 0) return null;

    return (
      <div className="regional-selection-summary mt-3">
        <div className="regional-selection-summary__header">
          <h6 className="regional-selection-summary__title">Selected Configuration</h6>
          <div className="d-flex align-items-center gap-2">
            <span className="regional-selection-summary__count">
              {selectedStatesData.length} state(s) selected
            </span>
            <button
              type="button"
              className="regional-selection-summary__clear btn btn-sm btn-outline-secondary"
              onClick={selection.clearSelection}
            >
              Clear All
            </button>
          </div>
        </div>
        
        <div className="state-tags mt-2">
          {selectedStatesData.map(state => (
            <span
              key={state.stateId}
              className={`state-tag ${
                selection.isPrimaryState(state.stateId) ? 'primary' : ''
              }`}
              onClick={() => {
                if (!selection.isPrimaryState(state.stateId)) {
                  selection.setPrimaryState(state.stateId);
                }
              }}
              style={{ cursor: selection.isPrimaryState(state.stateId) ? 'default' : 'pointer' }}
            >
              {state.stateName}
              {selection.isPrimaryState(state.stateId) && (
                <Icon icon="Star" className="ms-1" size="sm" />
              )}
              {!selection.isPrimaryState(state.stateId) && (
                <small className="ms-1 text-muted">(click to make primary)</small>
              )}
              <button
                type="button"
                className="state-tag__remove"
                onClick={(e) => {
                  e.stopPropagation();
                  selection.toggleState(state.stateId);
                }}
                aria-label={`Remove ${state.stateName}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        
        {primaryStateData && (
          <div className="mt-2">
            <small className="text-muted">
              Primary State: <strong>{primaryStateData.stateName}</strong>
            </small>
          </div>
        )}

        {/* Validation status */}
        <div className="mt-2">
          {selection.validateSelection() ? (
            <small className="text-success">
              <Icon icon="CheckCircle" className="me-1" size="sm" />
              Regional configuration is complete
            </small>
          ) : (
            <small className="text-warning">
              <Icon icon="Warning" className="me-1" size="sm" />
              Please complete all required fields
            </small>
          )}
        </div>
      </div>
    );
  };

  if (statesLoading || scopesLoading || prioritiesLoading) {
    return (
      <div className={`regional-selector ${className}`}>
        <div className="regional-loading">
          <Icon icon="CircularProgress" className="me-2" />
          Loading regional configuration...
        </div>
      </div>
    );
  }

  if (statesError || scopesError || prioritiesError) {
    return (
      <div className={`regional-selector ${className}`}>
        <div className="regional-error">
          <p className="regional-error__message">
            {statesError || scopesError || prioritiesError}
          </p>
          <Button
            color="danger"
            size="sm"
            className="regional-error__retry"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`regional-selector ${className}`}>
      <div className="regional-selector__header">
        <h5 className="regional-selector__title">Regional Targeting Configuration</h5>
        <div className="regional-selector__actions">
          <Button
            color="light"
            size="sm"
            onClick={selection.clearSelection}
            isDisable={selectedStatesData.length === 0}
          >
            Clear All
          </Button>
        </div>
      </div>

      {renderQuickSelectStates()}
      {renderAllStates()}
      {renderConfigurationOptions()}
      {renderSelectionSummary()}
    </div>
  );
};

export default RegionalSelector;
